import { IVector3 } from '@inter-ikea-betrakta/viewer';
import { ColliderUtils, ComponentIds, ConnectorUtils, HierarchyUtils, TransformUtils, Vector } from '@inter-ikea-core/core';
import { getIpexViewer } from '@inter-ikea-core/ipex-betrakta-core-adapter';
import { HideOccludingObjectsComponentIds } from './types';
import { FadeMaterialComponent } from './FadeMaterialComponent';
import { FadeAnimationComponent } from './FadeAnimationComponent';
import { SystemBase4 } from '@inter-ikea-core/core-toolbox';
const MILLIMETERS_IN_A_METER = 1000;
/**
 * @param entity The entity being checked.
 * @param initialTransform The transform the entity had when started moving.
 *  Optional, only needed for entities that are being moved.
 * @returns The direction the product is facing
 */
export function getEntityNormal(entity, initialTransform) {
    const currentNormal = TransformUtils.worldTransform(entity).forward.times(-1);
    /**
     * For automatically grouped entities we have to rely on the direction the
     * group is facing instead of the connectors. Each child of the group can
     * have a different connection direction so they would show/hide in different
     * ways
     */
    if (entity.hasComponent(ComponentIds.AUTOMATIC_ENTITY_GROUP_COMPONENT_ID)) {
        return currentNormal;
    }
    const guestConnectionsComponent = entity.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
    if (!guestConnectionsComponent?.highestPriorityConnection) {
        return currentNormal;
    }
    const { hostConnector, hostConnectorEntity } = guestConnectionsComponent.highestPriorityConnection;
    const { up, forward } = TransformUtils.worldTransform(hostConnectorEntity);
    let hostNormal;
    if (ConnectorUtils.isSurfaceConnector(hostConnector)) {
        /**
         * For room surfaces we know the UP vector is the normal. (because all room
         * surfaces identity transform is that of the floor)
         */
        hostNormal = up;
    }
    else if (ConnectorUtils.isEdgeConnector(hostConnector)) {
        /**
         * For edge connector normals we know we can remove the Y component
         * to lay it flat and point in the same direction as room surface normals.
         */
        hostNormal = new Vector(up.X, 0, up.Z).normalize();
    }
    else {
        hostNormal = forward.times(-1);
    }
    if (initialTransform) {
        /**
         * The host connector transforms are not updated while the entities are moved.
         * To get around that, we figure what is the rotation between the initial
         * and current transform, and apply it to the host connector transform.
         */
        const initialNormal = initialTransform.forward.times(-1);
        const rotationToNormal = initialNormal.getRotationTo(currentNormal);
        return hostNormal.rotate(rotationToNormal);
    }
    return hostNormal;
}
/**
 * Calculates the point that is used to check the entity direction against
 * the camera.
 * Instead of using the entity position that is generally placed at the
 * middle of the product, use the bounding box to figure out the point
 * where the object is attached to it's connector.
 */
export function getDirectionReferencePoint(entity, normal) {
    const AABB = ColliderUtils.axisAlignedBoundingBoxForHierarchy(entity);
    if (!AABB) {
        return TransformUtils.worldTransform(entity).position;
    }
    const { size, center } = AABB;
    // halfSizeDirection is the vector that goes from the center of the bounding box to the side
    // that the normal is pointing to.
    const halfSizeDirection = size.times(0.5).timesElementWise(normal);
    return center.minus(halfSizeDirection);
}
export function entityIsVisible(entity, viewer, initialTransform) {
    if (!viewer)
        return false;
    const cameraPositionMeters = IVector3.scale(viewer.get_camera_state().position, MILLIMETERS_IN_A_METER);
    const cameraPosition = new Vector(cameraPositionMeters.x, cameraPositionMeters.y, cameraPositionMeters.z);
    const normal = getEntityNormal(entity, initialTransform);
    const rotationReferencePoint = getDirectionReferencePoint(entity, normal);
    return normal.dot(cameraPosition.minus(rotationReferencePoint)) >= 0;
}
/**
 * The system checks the 3D objects in the scene and decides which ones should
 * be hidden from the user to make it easier to interact with the rest of the
 * room.
 */
export class HideOccludingObjectsSystem extends SystemBase4 {
    get viewer() {
        return getIpexViewer(this.coreServices.sceneManager);
    }
    constructor(coreServices, getFadeableObjects) {
        super(ComponentIds.IS_DRAGGED_COMPONENT_ID, HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID, ComponentIds.VIEWER_OBJECT_COMPONENT_ID, ComponentIds.IS_LOADING_SNAPSHOT_COMPONENT_ID);
        this.coreServices = coreServices;
        this.getFadeableObjects = getFadeableObjects;
        this.id = 'HideOccludingObjectsSystem';
        this.cameraUpdatedObserver = null;
        this.systemsAfterMe = [];
        this.systemsBeforeMe = [];
        this.doingChangesOnComponent = [];
    }
    enable() {
        super.enable();
        if (this.viewer) {
            this.cameraUpdatedObserver = this.viewer
                .get_camera_updated_observable()
                .add(() => this.updateVisibility());
            this.updateVisibility();
        }
    }
    disable() {
        this.cameraUpdatedObserver?.dispose();
        this.cameraUpdatedObserver = null;
        this.removeFadeMaterialComponentFromAllFadedEntities();
        super.disable();
    }
    onAddComponent(entity, _newComponent) {
        entity.removeComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID);
    }
    onRemoveComponent(entity, _oldComponent) {
        const fadeableObjectIds = this.getFadeableObjects().map(e => e.id);
        /**
         * Check the ancestors in case the dragged entity has been attached to
         * something that is facing away (i.e: shower set assembly).
         */
        const visibilityRoot = HierarchyUtils.getAncestors(entity)
            .reverse()
            .find(e => fadeableObjectIds.find(fadeableObjectId => e.id === fadeableObjectId) &&
            (e.hasComponent(ComponentIds.ASSEMBLY_STRUCTURE_COMPONENT_ID) ||
                e.hasComponent(ComponentIds.PRODUCT_COMPONENT_ID))) || entity;
        if (!entityIsVisible(visibilityRoot, this.viewer)) {
            visibilityRoot.removeComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID);
            visibilityRoot.addComponent(new FadeMaterialComponent());
        }
    }
    onAddComponent2(entity, _newComponent) {
        HierarchyUtils.children(entity).forEach(e => e.addComponent(new FadeMaterialComponent()));
    }
    onRemoveComponent2(entity, _oldComponent) {
        HierarchyUtils.children(entity).forEach(e => e.removeComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID));
    }
    onAddComponent3(entity, _newComponent) {
        const visibilityRoot = HierarchyUtils.getAncestors(entity)
            .reverse()
            .find(e => e.hasComponent(ComponentIds.ASSEMBLY_STRUCTURE_COMPONENT_ID) ||
            e.hasComponent(ComponentIds.SELECTABLE_COMPONENT_ID));
        /**
         * Fade the structure if it is already faded and gets something new attached
         */
        if (visibilityRoot &&
            visibilityRoot.id !== entity.id &&
            visibilityRoot.hasComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID)) {
            this.coreServices.sceneManager.scene.addComponent(new FadeAnimationComponent(true));
            visibilityRoot.removeComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID);
            visibilityRoot.addComponent(new FadeMaterialComponent());
        }
    }
    onRemoveComponent4(_entity, _newComponent) {
        this.removeFadeMaterialComponentFromAllFadedEntities();
        this.updateVisibility();
        this.coreServices.sceneManager.scene.addComponent(new FadeAnimationComponent(true));
    }
    removeFadeMaterialComponentFromAllFadedEntities() {
        this.coreServices.entityManager.entities
            .filter(entity => entity.hasComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID))
            .forEach(entity => {
            entity.removeComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID);
        });
    }
    updateVisibility() {
        this.getFadeableObjects().forEach(entity => {
            if (entityIsVisible(entity, this.viewer)) {
                entity.removeComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID);
            }
            else {
                entity.addComponent(new FadeMaterialComponent());
            }
        });
    }
}
//# sourceMappingURL=HideOccludingObjectsSystem.js.map